/**
 * masd.lab Theme JavaScript
 */

const MasdTheme = {
  /**
   * Initialize all theme functionality
   */
  init() {
    this.mobileMenu();
    this.cartDrawer();
    this.productForm();
    this.productGallery();
    this.quantityInputs();
    this.collectionSort();
  },

  /**
   * Mobile menu toggle
   */
  mobileMenu() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');
    const close = document.querySelector('[data-mobile-menu-close]');

    if (!toggle || !menu) return;

    const openMenu = () => {
      menu.classList.add('is-open');
      overlay?.classList.add('is-active');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    };

    const closeMenu = () => {
      menu.classList.remove('is-open');
      overlay?.classList.remove('is-active');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };

    toggle.addEventListener('click', openMenu);
    close?.addEventListener('click', closeMenu);
    overlay?.addEventListener('click', closeMenu);

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) {
        closeMenu();
      }
    });
  },

  /**
   * Cart drawer functionality
   */
  cartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-drawer-overlay]');
    const cartToggles = document.querySelectorAll('[data-cart-toggle]');
    const closeButtons = document.querySelectorAll('[data-cart-drawer-close]');

    if (!drawer) return;

    const openDrawer = () => {
      drawer.classList.add('is-open');
      overlay?.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      overlay?.classList.remove('is-active');
      document.body.style.overflow = '';
    };

    // Toggle cart drawer
    cartToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer();
      });
    });

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeDrawer);
    });

    overlay?.addEventListener('click', closeDrawer);

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // Handle add to cart forms
    this.handleAddToCart(openDrawer);
    this.handleCartUpdates();
  },

  /**
   * Handle add to cart via AJAX
   */
  handleAddToCart(openDrawer) {
    const forms = document.querySelectorAll('[data-product-form]');

    forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = form.querySelector('[data-add-to-cart]');
        const btnText = form.querySelector('[data-add-to-cart-text]');
        const originalText = btnText?.textContent;

        // Disable button and show loading
        submitBtn.disabled = true;
        if (btnText) btnText.textContent = 'Adding...';

        try {
          const formData = new FormData(form);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            // Update cart count and open drawer
            await this.updateCartCount();
            await this.updateCartDrawer();
            if (btnText) btnText.textContent = 'Added!';
            openDrawer();

            setTimeout(() => {
              if (btnText) btnText.textContent = originalText;
            }, 2000);
          } else {
            const error = await response.json();
            alert(error.description || 'Could not add to cart');
            if (btnText) btnText.textContent = originalText;
          }
        } catch (err) {
          console.error('Add to cart error:', err);
          if (btnText) btnText.textContent = originalText;
        } finally {
          submitBtn.disabled = false;
        }
      });
    });
  },

  /**
   * Handle cart quantity updates and removals
   */
  handleCartUpdates() {
    document.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('[data-remove-item]');
      const quantityMinus = e.target.closest('[data-quantity-minus]');
      const quantityPlus = e.target.closest('[data-quantity-plus]');

      const cartItem = e.target.closest('[data-cart-item]');
      if (!cartItem) return;

      const line = cartItem.dataset.line;
      const input = cartItem.querySelector('[data-quantity-input]');

      if (removeBtn) {
        await this.updateCartItem(line, 0);
      } else if (quantityMinus && input) {
        const newQty = Math.max(0, parseInt(input.value) - 1);
        input.value = newQty;
        await this.updateCartItem(line, newQty);
      } else if (quantityPlus && input) {
        const newQty = parseInt(input.value) + 1;
        input.value = newQty;
        await this.updateCartItem(line, newQty);
      }
    });

    // Handle quantity input change
    document.addEventListener('change', async (e) => {
      if (!e.target.matches('[data-cart-item] [data-quantity-input]')) return;

      const cartItem = e.target.closest('[data-cart-item]');
      const line = cartItem?.dataset.line;
      const quantity = parseInt(e.target.value);

      if (line && !isNaN(quantity)) {
        await this.updateCartItem(line, quantity);
      }
    });
  },

  /**
   * Update a cart line item
   */
  async updateCartItem(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(line), quantity })
      });

      if (response.ok) {
        await this.updateCartCount();
        await this.updateCartDrawer();
      }
    } catch (err) {
      console.error('Cart update error:', err);
    }
  },

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (err) {
      console.error('Cart count error:', err);
    }
  },

  /**
   * Update cart drawer contents
   */
  async updateCartDrawer() {
    try {
      const response = await fetch('/?sections=cart-drawer');
      const html = await response.json();

      if (html['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html['cart-drawer'], 'text/html');

        const newBody = doc.querySelector('[data-cart-drawer-body]');
        const newFooter = doc.querySelector('[data-cart-drawer-footer]');
        const currentBody = document.querySelector('[data-cart-drawer-body]');
        const currentFooter = document.querySelector('[data-cart-drawer-footer]');

        if (newBody && currentBody) {
          currentBody.innerHTML = newBody.innerHTML;
        }
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
          currentFooter.style.display = newFooter.style.display;
        }
      }
    } catch (err) {
      console.error('Cart drawer update error:', err);
    }
  },

  /**
   * Product form - variant selection
   */
  productForm() {
    const form = document.querySelector('[data-product-form]');
    const productJson = document.querySelector('[data-product-json]');

    if (!form || !productJson) return;

    let product;
    try {
      product = JSON.parse(productJson.textContent);
    } catch (e) {
      return;
    }

    const selects = form.querySelectorAll('[data-option-select]');
    const variantIdInput = form.querySelector('[data-variant-id]');
    const priceEl = document.querySelector('[data-product-price]');
    const addToCartBtn = form.querySelector('[data-add-to-cart]');
    const addToCartText = form.querySelector('[data-add-to-cart-text]');

    const getSelectedOptions = () => {
      return Array.from(selects).map(select => select.value);
    };

    const findVariant = (options) => {
      return product.variants.find(variant => {
        return options.every((option, index) => {
          return variant.options[index] === option;
        });
      });
    };

    const updateVariant = () => {
      const options = getSelectedOptions();
      const variant = findVariant(options);

      if (variant) {
        // Update hidden input
        variantIdInput.value = variant.id;

        // Update URL
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        // Update price
        if (priceEl) {
          if (variant.available) {
            if (variant.compare_at_price > variant.price) {
              priceEl.innerHTML = `
                <div class="price">
                  <span class="price__sale">${this.formatMoney(variant.price)}</span>
                  <span class="price__compare">${this.formatMoney(variant.compare_at_price)}</span>
                </div>
              `;
            } else {
              priceEl.innerHTML = `
                <div class="price">
                  <span class="price__regular">${this.formatMoney(variant.price)}</span>
                </div>
              `;
            }
          } else {
            priceEl.innerHTML = `
              <div class="price price--sold-out">
                <span class="price__sold-out">Sold out</span>
              </div>
            `;
          }
        }

        // Update button
        if (addToCartBtn && addToCartText) {
          if (variant.available) {
            addToCartBtn.disabled = false;
            addToCartText.textContent = 'Add to cart';
          } else {
            addToCartBtn.disabled = true;
            addToCartText.textContent = 'Sold out';
          }
        }

        // Update main image if variant has image
        if (variant.featured_image) {
          const mainImage = document.querySelector('[data-product-image]');
          if (mainImage) {
            mainImage.src = variant.featured_image.src;
          }
        }
      }
    };

    selects.forEach(select => {
      select.addEventListener('change', updateVariant);
    });
  },

  /**
   * Product gallery - thumbnail clicks
   */
  productGallery() {
    const thumbnails = document.querySelectorAll('[data-product-thumbnail]');
    const mainImage = document.querySelector('[data-product-image]');

    if (!thumbnails.length || !mainImage) return;

    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const imageUrl = thumb.dataset.imageUrl;
        if (imageUrl) {
          mainImage.src = imageUrl;

          // Update active state
          thumbnails.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        }
      });
    });
  },

  /**
   * Quantity input controls
   */
  quantityInputs() {
    document.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-quantity-minus]');
      const plus = e.target.closest('[data-quantity-plus]');

      if (!minus && !plus) return;

      const wrapper = e.target.closest('.quantity');
      const input = wrapper?.querySelector('[data-quantity-input]');

      if (!input) return;

      // Skip if this is inside a cart item (handled by cart updates)
      if (e.target.closest('[data-cart-item]')) return;

      const currentVal = parseInt(input.value) || 1;
      const min = parseInt(input.min) || 1;

      if (minus) {
        input.value = Math.max(min, currentVal - 1);
      } else if (plus) {
        input.value = currentVal + 1;
      }
    });
  },

  /**
   * Collection page sorting
   */
  collectionSort() {
    const sortSelect = document.querySelector('[data-sort-select]');

    if (!sortSelect) return;

    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location = url;
    });
  },

  /**
   * Format money helper
   */
  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => MasdTheme.init());
